import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { nanoid } from 'nanoid';

import type { RHFGroupFieldValue } from '@/components/dynamic-filter/FilterFormGroup';
import type { TabLink } from '@/types/dynamic-filter.type';
import type { KeyFilter, KeyWithFilter } from '@/utils/filter/filter';
import type { FormQuery } from '@/utils/filter/inputType/inputTypeManager';
import { t } from '@/utils/lang';
import { removeTransfer } from './transfer';

export interface ListFieldsState {
    id: string;
    key: string;
    label: string;
    inputType: KeyWithFilter['type'];
    value: any[] | string | boolean;
    keyService?: TabLink;
    defaultOperator: KeyFilter['defaultOperator'];
    multipleSelect?: boolean;
}

interface DynamicFilterState {
    listFields: ListFieldsState[];
    dynamicFilter: Record<string, any>;
    formQuery: string;
    graphqlQuery: string;
}

type DictDynamicFilter = Record<string, DynamicFilterState>;

type ActionWithKeySlice<T> = { actionData: T } & {
    keySlice: string;
    additionalWhere?: string;
};

const initialState: DictDynamicFilter = {};

export const cleanupDynamicFilter = (keySlice: string) => (dispatch: any) => {
    dispatch(removeDynamicFilterAction({ keySlice, actionData: '' }));
    dispatch(removeTransfer(''));
};

export const applyDynamicFilter = <T>(
    state: Record<string, any>,
    keySlice: string,
    data: Partial<T>
) => {
    const slice = state[keySlice];

    if (!state[keySlice]) {
        state[keySlice] = {
            listFields: [],
            dynamicFilter: {},
        };
    }

    for (const k in data) {
        slice.dynamicFilter[k] = (data as any)[k];
    }
};

export const dynamicFilterSlice = createSlice({
    name: 'dynamicFilter',
    initialState,
    reducers: {
        formatListFieldsAction: (
            state,
            action: PayloadAction<ActionWithKeySlice<KeyWithFilter[]>>
        ) => {
            const fields: ListFieldsState[] = [];

            action.payload.actionData.forEach(field => {
                field.key.forEach(key => {
                    const dataKey = (() => {
                        if (typeof key === 'object') {
                            return {
                                label: t(key.trans, 'DynamicFilter'),
                                key: key.key,
                                keyService:
                                    key.keyService || (key.key as TabLink),
                                defaultOperator: key.defaultOperator,
                                multipleSelect: key.multipleSelect,
                            };
                        }

                        return { label: t(key, 'DynamicFilter'), key: key };
                    })();

                    fields.push({
                        id: nanoid(),
                        key: dataKey.key,
                        label: dataKey.label,
                        inputType: field.type,
                        value: field.value!,
                        keyService: dataKey.keyService,
                        defaultOperator: dataKey.defaultOperator!,
                        multipleSelect: dataKey.multipleSelect,
                    });
                });
            });

            if (!state[action.payload.keySlice]) {
                state[action.payload.keySlice] = {
                    listFields: fields,
                    dynamicFilter: {},
                    formQuery: '',
                    graphqlQuery: '',
                };
            } else {
                state[action.payload.keySlice].listFields = fields;
            }
        },

        /**
         * Using for dynamic filter
         */
        addDynamicFilterAction: (
            state,
            action: PayloadAction<ActionWithKeySlice<FormQuery>>
        ) => {
            applyDynamicFilter(
                state,
                action.payload.keySlice,
                action.payload.actionData
            );
        },

        replaceDynamicFilterAction: (
            state,
            action: PayloadAction<ActionWithKeySlice<FormQuery>>
        ) => {
            if (!state[action.payload.keySlice]) {
                state[action.payload.keySlice] = {
                    listFields: [],
                    dynamicFilter: {},
                    formQuery: '',
                    graphqlQuery: '',
                };
            }

            const filterObj = {} as any;

            if (action.payload.actionData?.searchInput) {
                filterObj['searchInput'] =
                    action.payload.actionData.searchInput;
            } else {
                filterObj['searchInput'] = {};
            }

            Object.assign(filterObj, action.payload.actionData);

            state[action.payload.keySlice].dynamicFilter = {
                ...state[action.payload.keySlice].dynamicFilter,
                ...filterObj,
            };
        },

        addSearchFilterAction: (
            state,
            action: PayloadAction<
                ActionWithKeySlice<{
                    keyword: string;
                    defaultField?: string | Record<string, RHFGroupFieldValue>;
                }>
            >
        ) => {
            const defaultField =
                (action.payload.actionData.defaultField as any) || {};

            let formQueryFormat: Record<string, RHFGroupFieldValue> =
                defaultField;

            if (typeof defaultField === 'string') {
                formQueryFormat = {
                    searchInput: {
                        id: 'searchInput',
                        key: defaultField || 'searchInput',
                        operator: 'like',
                        value: action.payload.actionData.keyword,
                        inputType: 'input',
                    },
                };
            }

            if (!state[action.payload.keySlice]) {
                state[action.payload.keySlice] = {
                    listFields: [],
                    dynamicFilter: {},
                    formQuery: '',
                    graphqlQuery: '',
                };
            }

            applyDynamicFilter(
                state,
                action.payload.keySlice,
                formQueryFormat as any
            );
        },

        removeDynamicFilterItemAction: (
            state,
            action: PayloadAction<ActionWithKeySlice<string>>
        ) => {
            delete state[action.payload.keySlice].dynamicFilter[
                action.payload.actionData
            ];
        },

        clearDynamicFilterAction: (
            state,
            action: PayloadAction<ActionWithKeySlice<{}>>
        ) => {
            for (const key in state[action.payload.keySlice].dynamicFilter) {
                if (key.includes('searchInput')) {
                    continue;
                }

                delete state[action.payload.keySlice].dynamicFilter[key];
            }
        },

        removeDynamicFilterAction: (
            state,
            action: PayloadAction<ActionWithKeySlice<string>>
        ) => {
            delete state[action.payload.keySlice];
        },
    },
});

export const {
    formatListFieldsAction,
    addDynamicFilterAction,
    clearDynamicFilterAction,
    addSearchFilterAction,
    removeDynamicFilterAction,
    removeDynamicFilterItemAction,
    replaceDynamicFilterAction,
} = dynamicFilterSlice.actions;

export default dynamicFilterSlice.reducer;
